import React, { Dispatch, SetStateAction } from "react";

import { TFunction, useGetFarms, useTranslation } from "utils";

import { ACCOUNTING_METHODOLOGY, PRACTICE_BUNDLE_SLUG, PracticeBundle, VIEW_TYPE } from "models";

import { Flex, Icon, Switch, Tooltip, TooltipWrapper } from "components";

type PracticeBundleControlsProps = {
  bundles: PracticeBundle[];
  optionalInputsAreShown: boolean;
  setOptionalInputsAreShown: Dispatch<SetStateAction<boolean>>;
  onBundleActiveStateChange?: (bundleSlug: PRACTICE_BUNDLE_SLUG, value: boolean) => void;
  isCurrentYear: boolean;
  onPastYearToggle?: () => void;
  hideOptionalPractices?: boolean;
  hidePracticeIcons?: boolean;
  viewType?: VIEW_TYPE;
  isHeader?: boolean;
};

const getIconProperties = (bundle: PracticeBundle, isCurrentYear: boolean, t: TFunction, isFieldTimelineHeader?: boolean) => {
  const getPracticeStringFromBundle = () =>
    bundle.slug === "tillage" ? t("fieldTimeline:impactPanel.tillageSeeding") : t("fieldTimeline:impactPanel.fertilizers");

  if (!isFieldTimelineHeader && !bundle.enrollable) {
    return {
      className: bundle.active ? "bundle-icon active-unavailable" : "bundle-icon inactive-unavailable",
      tooltipText: bundle.active
        ? t("cropAddEditDrawer.practiceIcons.removingPracticeDisabled", { practice: getPracticeStringFromBundle() })
        : t("cropAddEditDrawer.practiceIcons.addingPracticeDisabled", { practice: getPracticeStringFromBundle() }),
    };
  }

  if (isFieldTimelineHeader || bundle.hasConfirmedData) {
    return {
      className: "bundle-icon submitted",
      tooltipText: t("cropAddEditDrawer.practiceIcons.addedSinceTooltip", { practice: getPracticeStringFromBundle(), year: bundle.startYear }),
    };
  }

  if (isCurrentYear) {
    if (bundle.active) {
      return { className: "bundle-icon active", tooltipText: t("cropAddEditDrawer.practiceIcons.removeTooltip", { practice: getPracticeStringFromBundle() }) };
    }

    return { className: "bundle-icon inactive", tooltipText: t("cropAddEditDrawer.practiceIcons.addTooltip", { practice: getPracticeStringFromBundle() }) };
  }

  if (bundle.active) {
    return {
      className: "bundle-icon active-unavailable",
      tooltipText: t("cropAddEditDrawer.practiceIcons.addedSinceTooltip", { practice: getPracticeStringFromBundle(), year: bundle.startYear }),
    };
  }

  return {
    className: "bundle-icon inactive-unavailable",
    tooltipText: t("cropAddEditDrawer.practiceIcons.canAddTooltip", { practice: getPracticeStringFromBundle() }),
  };
};

export const BUNDLE_PRIORITY = { crops: 0, fertilizer: 1, tillage: 2 };

export const PracticeBundleControls = ({
  bundles,
  hideOptionalPractices = true,
  hidePracticeIcons = false,
  isCurrentYear,
  optionalInputsAreShown,
  setOptionalInputsAreShown,
  onBundleActiveStateChange,
  onPastYearToggle,
  viewType,
  isHeader,
}: PracticeBundleControlsProps) => {
  const { t } = useTranslation(["allFieldView", "fieldTimeline"]);
  const { currentFarm } = useGetFarms();

  const handleToggleBundleActiveState = (bundleSlug: PRACTICE_BUNDLE_SLUG, value: boolean) => {
    const relevantBundle = bundles.find((bundle) => bundle.slug === bundleSlug);

    if (!relevantBundle || relevantBundle.hasConfirmedData || !relevantBundle.enrollable) {
      return;
    }

    onBundleActiveStateChange?.(bundleSlug, value);
  };

  const isSingleField = viewType === VIEW_TYPE.SINGLE_FIELD;
  // Checks if timeline header or drawer icons
  const isFieldTimelineHeader = isSingleField && isHeader;

  const isDisabledOrHasOptionalPracticesHidden = !isCurrentYear || hideOptionalPractices;
  const showOptionalInputToggle = !isFieldTimelineHeader && !hideOptionalPractices;

  // Tooltip copy changes according to the farm program (intervention or inventory)
  const optionalToggleInputTooltip =
    currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.intervention ? (
      <>
        <span className="white-text">{t("cropAddEditDrawer.showOptionalInputsTooltip1")}</span>
        <br />
        <br />
        <span className="white-text">{t("cropAddEditDrawer.showOptionalInputsTooltip2")}</span>
      </>
    ) : (
      <span className="white-text">{t("cropAddEditDrawer.showOptionalInputsTooltipInventory")}</span>
    );

  if (viewType === VIEW_TYPE.SIMULATION) {
    return;
  }

  return (
    <Flex align="center" justify="space-between" className="margin-top-150">
      <Flex gap={4}>
        {!hidePracticeIcons &&
          bundles
            .filter((bundle) => bundle.slug !== "crops")
            .sort((a, b) => BUNDLE_PRIORITY[a.slug] - BUNDLE_PRIORITY[b.slug])
            .map((bundle) =>
              isSingleField && !bundle.active ? null : (
                <TooltipWrapper
                  className="practice-bundle-tooltip"
                  data-testid={`practice-bundle-${bundle.slug}-tooltip`}
                  key={bundle.slug}
                  tooltip={{
                    title: (
                      <span className="bundle-icon-tooltip-content">{getIconProperties(bundle, isCurrentYear, t, isFieldTimelineHeader).tooltipText}</span>
                    ),
                    placement: "top",
                  }}
                >
                  <span
                    key={bundle.slug}
                    className={getIconProperties(bundle, isCurrentYear, t, isFieldTimelineHeader).className}
                    data-testid={`practice-bundle-${bundle.slug}-icon-container`}
                  >
                    <Icon
                      dataTestId={`practice-bundle-${bundle.slug}-icon`}
                      iconName={`klim/${bundle.slug}`}
                      onClick={isCurrentYear && !isFieldTimelineHeader ? () => handleToggleBundleActiveState(bundle.slug, !bundle.active) : () => {}}
                    />
                  </span>
                </TooltipWrapper>
              )
            )}
      </Flex>

      {showOptionalInputToggle && (
        <Flex align="center" gap={8} className="margin-left-auto">
          <Tooltip title={optionalToggleInputTooltip}>
            <Flex align="center" gap={4}>
              <span className="paragraph-m">{t("cropAddEditDrawer.showOptionalInputs")}</span>
              <Icon iconName="tabler/info-circle" size="s" />
            </Flex>
          </Tooltip>
          <Switch
            defaultChecked={isCurrentYear}
            checked={optionalInputsAreShown}
            data-testid="optional-input-toggle-switch"
            onChange={() => {
              setOptionalInputsAreShown(!optionalInputsAreShown);
              if (isDisabledOrHasOptionalPracticesHidden) {
                onPastYearToggle?.();
              }
            }}
          />
        </Flex>
      )}
    </Flex>
  );
};
